import React, { useEffect, useMemo, useState } from "react";
import { apiDelete, apiGet, apiPost } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./DrugSupplierNotesPanel.css";

type DrugSupplierNote = {
  id: number;
  drug_name: string;
  notes: string;
  created_at?: string;
  updated_at?: string;
};

export default function DrugSupplierNotesPanel() {
  const { showToast } = useToast();
  const [items, setItems] = useState<DrugSupplierNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<number | null>(null);

  const [drugName, setDrugName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const activeItem = useMemo(
    () => (activeId != null ? items.find((i) => i.id === activeId) ?? null : null),
    [activeId, items]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => (i.drug_name || "").toLowerCase().includes(q));
  }, [items, query]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet<DrugSupplierNote[]>("/api/pharmacy/drug-notes");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (activeItem) {
      setDrugName(activeItem.drug_name || "");
      setNotes(activeItem.notes || "");
    }
  }, [activeItem]);

  const startNew = () => {
    setActiveId(null);
    setDrugName("");
    setNotes("");
  };

  const handleSave = async () => {
    const name = drugName.trim();
    if (!name) {
      showToast({ message: "Please enter a drug name", type: "warning" });
      return;
    }
    setSaving(true);
    try {
      const saved = await apiPost<DrugSupplierNote>("/api/pharmacy/drug-notes", {
        drug_name: name,
        notes,
      });
      if (saved?.id) {
        setItems((prev) => {
          const next = prev.filter((p) => p.id !== saved.id);
          return [saved, ...next];
        });
        setActiveId(saved.id);
        showToast({ message: "Note saved successfully", type: "success" });
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      showToast({ message: err?.message || "Failed to save note", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (activeId == null) return;
    if (!window.confirm("Delete this note?")) return;
    try {
      await apiDelete(`/api/pharmacy/drug-notes/${activeId}`);
      setItems((prev) => prev.filter((p) => p.id !== activeId));
      startNew();
      showToast({ message: "Note deleted", type: "success" });
    } catch (e: unknown) {
      const err = e as { message?: string };
      showToast({ message: err?.message || "Failed to delete note", type: "error" });
    }
  };

  return (
    <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div className="mod-window-header" style={{ flexShrink: 0, paddingBottom: '16px', borderBottom: '1px solid #e2e8f0', marginBottom: 0 }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem', color: '#0f172a' }}>
          📓 Drug Supplier Notes
        </h2>
        <p className="mod-text-muted" style={{ margin: '4px 0 0 0', fontSize: '0.9rem' }}>Manage supplier and contact details for medications</p>
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '300px 1fr', overflow: 'hidden' }}>
        {/* Left pane: list */}
        <div style={{ background: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', borderBottom: '1px solid #e2e8f0' }}>
            <Button variant="primary" onClick={startNew} style={{ width: '100%' }}>+ New Note</Button>
            <input
              className="mod-input"
              placeholder="Search drug..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="mod-empty-state" style={{ padding: '24px', fontSize: '0.85rem' }}>
                No notes found. Create one.
              </div>
            ) : (
              filtered.map((i) => {
                const active = i.id === activeId;
                return (
                  <div
                    key={i.id}
                    onClick={() => setActiveId(i.id)}
                    style={{ padding: '12px 16px', background: active ? '#fff' : 'transparent', border: active ? '1px solid #cbd5e1' : '1px solid transparent', borderRadius: '8px', cursor: 'pointer', boxShadow: active ? '0 2px 4px rgba(0,0,0,0.02)' : 'none', transition: 'all 0.2s' }}
                  >
                    <div style={{ fontWeight: 600, color: active ? '#0f172a' : '#475569', fontSize: '0.95rem', marginBottom: '4px' }}>{i.drug_name}</div>
                    {i.updated_at && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Updated: {new Date(i.updated_at).toLocaleDateString()}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right pane: editor */}
        <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', background: '#fff', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>{activeId != null ? drugName || "Edit Note" : "New Note"}</h3>
            <div style={{ display: 'flex', gap: '12px' }}>
              {activeId != null && (
                <Button variant="outline" onClick={handleDelete} style={{ color: '#ef4444', borderColor: '#fee2e2', background: '#fef2f2' }}>
                  Delete
                </Button>
              )}
              <Button variant="primary" onClick={handleSave} isLoading={saving}>
                {saving ? "Saving..." : "Save Note"}
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Drug name</label>
            <input className="mod-input" value={drugName} onChange={e => setDrugName(e.target.value)} placeholder="e.g. Amoxicillin 500mg" />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Notes (supplier / alternate supplier / contact person / phone)</label>
            <textarea
              className="mod-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={`Supplier:\nAlternate supplier:\nContact person:\nPhone:\nOther:`}
              style={{ flex: 1, resize: 'none', minHeight: '300px', lineHeight: 1.6 }}
            />
          </div>
          
          <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', fontSize: '0.85rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' }}>
            💡 Tip: Keep phone numbers and alternate contact details here for quick reference during ordering.
          </div>
        </div>
      </div>
    </div>
  );
}

